import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Container, Typography, Box, Button, Grid, Paper, CircularProgress } from "@mui/material";
import { API_URL } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import heroImg from "../assets/images/hero-bg.jpg";

function MachineImage({ src, alt }: { src: string; alt: string }) {
  const [portrait, setPortrait] = useState(false);

  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      onLoad={(e: React.SyntheticEvent<HTMLImageElement>) => setPortrait(e.currentTarget.naturalHeight > e.currentTarget.naturalWidth)}
      sx={{
        width: "100%",
        height: portrait ? 320 : 220,
        objectFit: portrait ? "contain" : "cover",
        backgroundColor: portrait ? "#1a1a1a" : "transparent",
        display: "block",
      }}
    />
  );
}

export default function Home() {
  const { t, lang } = useLang();
  const [machines, setMachines] = useState<Array<{ model: string; desc: string; img: string; slug: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/products?lang=${lang}`)
      .then((res) => res.json())
      .then((data) => {
        setMachines(data.map((p: any) => ({
          model: p.title,
          desc: p.subtitle || "",
          img: p.images.length > 0 ? `${API_URL}${p.images.find((img: any) => img.id === p.hero_image_id)?.url || p.images[0].url}` : "",
          slug: p.slug,
        })));
      })
      .catch(() => { setMachines([]); })
      .finally(() => setLoading(false));
  }, [lang]);
  return (
    <>
      {/* HERO */}
      <Box
        sx={{
          height: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#f0f0f0",
          textAlign: "center",
          px: 2,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${heroImg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.25,
            animation: "heroZoom 20s ease-in-out infinite alternate",
            "@keyframes heroZoom": {
              "0%": { transform: "scale(1)" },
              "100%": { transform: "scale(1.08)" },
            },
          }}
        />
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Typography variant="h2" gutterBottom>
            {t("home.heroTitle")}
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.85 }}>
            {t("home.heroSubtitle")}
          </Typography>
          <Button
            component={Link}
            to="/oferta"
            variant="contained"
            size="large"
            sx={{
              mt: 4,
              backgroundColor: "#1976d2",
              color: "white",
              "&:hover": { backgroundColor: "#125ea8" },
            }}
          >
            {t("home.heroCta")}
          </Button>
        </Box>
      </Box>

      {/* MASZYNY */}
      <Container sx={{ py: 8, color: "#f0f0f0" }}>
        <Typography variant="h4" gutterBottom>
          {t("home.machinesTitle")}
        </Typography>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress sx={{ color: "#f0f0f0" }} />
          </Box>
        ) : (
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {machines.map((m) => (
              <Grid item xs={12} md={6} key={m.slug}>
                <Paper
                  component={Link}
                  to="/oferta"
                  sx={{
                    backgroundColor: "#2f2f2f",
                    color: "#f0f0f0",
                    textDecoration: "none",
                    display: "block",
                    transition: "0.2s",
                    "&:hover": { backgroundColor: "#3a3a3a" },
                    overflow: "hidden",
                  }}
                >
                  {m.img && (
                    <MachineImage src={m.img} alt={m.model} />
                  )}
                  <Box sx={{ p: 3 }}>
                    <Typography variant="h6">{m.model}</Typography>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.85 }}>
                      {m.desc}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </>
  );
}
